import {
  ArrowSquareOutIcon,
  MapTrifoldIcon,
  NavigationArrowIcon,
} from "@phosphor-icons/react";
import { useI18N } from "common/hooks";

type DirectionsLinkProps = {
  venueName: string;
  latitude: number;
  longitude: number;
  /** Google place id; pins the destination to the listing, not just the coordinates. */
  placeId?: string;
  /**
   * The visible text, e.g. "0.4 mile". Left out while the distance is not yet
   * measured from the user's own location: the address row then shows the
   * address alone, and without an address nothing is rendered.
   */
  distanceLabel?: string;
  /**
   * The street address. When set, the link is the venue page's address row
   * (Figma 16437:72833): map icon, "0.1 mile • address" with the address
   * underlined, and an external-link icon. Without it, just the distance.
   */
  address?: string;
  className?: string;
};

/**
 * Distance (and optionally the address) as a link that opens directions in
 * the device's maps app. Google's universal directions URL is used on every
 * platform: it hands off to the Maps app on phones and opens Google Maps in a
 * tab on desktop. It is also SSR-safe, unlike picking Apple vs Google from the
 * user agent.
 */
export function DirectionsLink({
  venueName,
  latitude,
  longitude,
  placeId,
  distanceLabel,
  address,
  className = "",
}: DirectionsLinkProps) {
  const i18n = useI18N();
  const params = new URLSearchParams({
    api: "1",
    destination: `${latitude},${longitude}`,
  });
  if (placeId) params.set("destination_place_id", placeId);
  const href = `https://www.google.com/maps/dir/?${params.toString()}`;
  const focusRing =
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus rounded-sm";

  if (address) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        // Starts with the visible text so voice-control users can say what
        // they see (WCAG 2.5.3).
        aria-label={
          distanceLabel
            ? i18n.t("getDirectionsWithAddressLinkLabel", {
                distance: distanceLabel,
                address,
                name: venueName,
              })
            : i18n.t("getDirectionsToAddressLinkLabel", {
                address,
                name: venueName,
              })
        }
        // min-h-6 keeps the target at the 24px WCAG 2.5.8 minimum.
        className={`group flex min-h-6 items-center gap-2 label-s-medium text-fg-neutral-subtle ${focusRing} ${className}`}
      >
        <MapTrifoldIcon
          size={16}
          aria-hidden="true"
          focusable="false"
          className="shrink-0"
        />
        {/* One line with the cut in the middle (CSS only ellipsizes the
            end): the head shrinks and ellipsizes, the tail stays. The full
            address is in aria-label and the tooltip. */}
        <span className="flex min-w-0 items-baseline" title={address}>
          {distanceLabel && (
            <span className="shrink-0 whitespace-pre">{distanceLabel} • </span>
          )}
          <MiddleEllipsis
            text={address}
            className="underline underline-offset-2 group-hover:no-underline"
          />
        </span>
        <ArrowSquareOutIcon
          size={16}
          aria-hidden="true"
          focusable="false"
          className="shrink-0"
        />
      </a>
    );
  }

  if (!distanceLabel) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={i18n.t("getDirectionsLinkLabel", {
        distance: distanceLabel,
        name: venueName,
      })}
      className={`inline-flex min-h-6 items-center gap-1 underline underline-offset-2 hover:no-underline ${focusRing} ${className}`}
    >
      <NavigationArrowIcon size={16} aria-hidden="true" focusable="false" />
      {distanceLabel}
    </a>
  );
}

/** Characters kept after the cut, roughly a postcode. */
const MIDDLE_ELLIPSIS_TAIL = 12;

/**
 * Single-line text that, when it overflows, loses its middle rather than its
 * end. The tail starts at a word boundary so the kept part reads cleanly.
 */
function MiddleEllipsis({
  text,
  className = "",
}: {
  text: string;
  className?: string;
}) {
  const cut = text.lastIndexOf(" ", text.length - MIDDLE_ELLIPSIS_TAIL);
  const split =
    text.length > MIDDLE_ELLIPSIS_TAIL * 2
      ? cut > 0
        ? cut
        : text.length - MIDDLE_ELLIPSIS_TAIL
      : -1;
  if (split < 0) {
    return <span className={`min-w-0 truncate ${className}`}>{text}</span>;
  }
  return (
    <span className="flex min-w-0">
      <span className={`min-w-0 truncate ${className}`}>
        {text.slice(0, split)}
      </span>
      {/* whitespace-pre keeps the space the tail starts with. */}
      <span className={`shrink-0 whitespace-pre ${className}`}>
        {text.slice(split)}
      </span>
    </span>
  );
}
